import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { enqueueSnackbar } from 'notistack';
import { AdvisorBooking, AdvisorBookingsResponse, AdvisorGrantStatus, MeetingType } from '../../types/advisorSupport';
import { AppDispatch, RootState } from '../store';
import { RequestStatusObject, createRequestStatusObject, buildReducers } from '../utils';
import i18n from '../../i18n';

export interface AdvisorSupportState {
	meetingTypes: MeetingType[];
	bookings: AdvisorBooking[];
	bookingsNextToken: string | null;
	selectedBooking: AdvisorBooking | null;
	grantStatus: AdvisorGrantStatus | null;

	meetingTypesRequestStatus: RequestStatusObject;
	bookingsRequestStatus: RequestStatusObject;
	bookingDetailRequestStatus: RequestStatusObject;
	grantStatusRequestStatus: RequestStatusObject;
	cancelBookingRequestStatus: RequestStatusObject;

	// Admin
	adminBookings: AdvisorBooking[];
	adminBookingsNextToken: string | null;
	adminSelectedBooking: AdvisorBooking | null;
	adminBookingsRequestStatus: RequestStatusObject;
	adminBookingDetailRequestStatus: RequestStatusObject;
	adminActionRequestStatus: RequestStatusObject;
}

const initialState: AdvisorSupportState = {
	meetingTypes: [],
	bookings: [],
	bookingsNextToken: null,
	selectedBooking: null,
	grantStatus: null,

	meetingTypesRequestStatus: createRequestStatusObject(),
	bookingsRequestStatus: createRequestStatusObject(),
	bookingDetailRequestStatus: createRequestStatusObject(),
	grantStatusRequestStatus: createRequestStatusObject(),
	cancelBookingRequestStatus: createRequestStatusObject(),

	// Admin
	adminBookings: [],
	adminBookingsNextToken: null,
	adminSelectedBooking: null,
	adminBookingsRequestStatus: createRequestStatusObject(),
	adminBookingDetailRequestStatus: createRequestStatusObject(),
	adminActionRequestStatus: createRequestStatusObject()
};

export const fetchMeetingTypes = createAsyncThunk<MeetingType[], void, { dispatch: AppDispatch; state: RootState }>(
	'advisorSupport/fetchMeetingTypes',
	async (_, { getState }) => {
		const state = getState();
		const currencyCode = state.user.currentUser?.currency || 'usd';

		const response = await API.get('inspirusapi', '/products', {
			headers: {},
			response: true,
			queryStringParameters: { category: 'advisorSession' }
		});

		const products = response.data.items || response.data;
		return products.map((p: any) => ({
			id: p.id,
			slug: p.slug,
			name: p.name,
			description: p.description,
			calendlyEventTypeUrl: p.calendlyEventTypeUrl,
			cancellationNoticeHours: p.cancellationNoticeHours ?? 2,
			price: typeof p.monthlyPrice === 'object' ? p.monthlyPrice?.[currencyCode] || 0 : p.monthlyPrice || 0,
			duration: p.durationMinutes || 0,
			category: 'advisorSession' as const,
			imageUrl: p.productLogo || p.productColor
		}));
	}
);

export const fetchUserBookings = createAsyncThunk<
	AdvisorBookingsResponse,
	{ status?: string; limit?: number; nextToken?: string | null },
	{ dispatch: AppDispatch; state: RootState }
>('advisorSupport/fetchUserBookings', async ({ status, limit, nextToken }) => {
	const queryStringParameters: Record<string, string> = {};
	if (status) queryStringParameters.status = status;
	if (limit) queryStringParameters.limit = String(limit);
	if (nextToken) queryStringParameters.nextToken = nextToken;

	const response = await API.get('inspirusapi', '/advisor/bookings', {
		headers: {},
		response: true,
		queryStringParameters
	});

	return response.data;
});

export const fetchBookingDetail = createAsyncThunk<
	AdvisorBooking,
	{ bookingId: string },
	{ dispatch: AppDispatch; state: RootState }
>('advisorSupport/fetchBookingDetail', async ({ bookingId }) => {
	const response = await API.get('inspirusapi', `/advisor/bookings/${bookingId}`, {
		headers: {},
		response: true,
		queryStringParameters: {}
	});

	return response.data;
});

export const fetchGrantStatus = createAsyncThunk<AdvisorGrantStatus, void, { dispatch: AppDispatch; state: RootState }>(
	'advisorSupport/fetchGrantStatus',
	async () => {
		const response = await API.get('inspirusapi', '/advisor/grants', {
			headers: {},
			response: true,
			queryStringParameters: {}
		});

		return response.data;
	}
);

export const cancelBooking = createAsyncThunk<
	AdvisorBooking,
	{ bookingId: string; reason?: string },
	{ dispatch: AppDispatch; state: RootState }
>('advisorSupport/cancelBooking', async ({ bookingId, reason }) => {
	const response = await API.post('inspirusapi', `/advisor/bookings/${bookingId}/cancel`, {
		headers: {},
		response: true,
		body: { reason }
	});

	return response.data;
});

// Admin thunks
export const fetchAdminBookings = createAsyncThunk<
	AdvisorBookingsResponse & { isNextPage: boolean },
	{ status?: string; limit?: number; nextToken?: string | null; startDate?: string; endDate?: string },
	{ dispatch: AppDispatch; state: RootState }
>('advisorSupport/fetchAdminBookings', async ({ status, limit, nextToken, startDate, endDate }) => {
	const queryStringParameters: Record<string, string> = {};
	if (status) queryStringParameters.status = status;
	if (limit) queryStringParameters.limit = String(limit);
	if (nextToken) queryStringParameters.nextToken = nextToken;
	if (startDate) queryStringParameters.startDate = startDate;
	if (endDate) queryStringParameters.endDate = endDate;

	const response = await API.get('inspirusapi', '/advisor/admin/bookings', {
		headers: {},
		response: true,
		queryStringParameters
	});

	return { ...response.data, isNextPage: !!nextToken };
});

export const fetchAdminBookingDetail = createAsyncThunk<
	AdvisorBooking,
	{ bookingId: string },
	{ dispatch: AppDispatch; state: RootState }
>('advisorSupport/fetchAdminBookingDetail', async ({ bookingId }) => {
	const response = await API.get('inspirusapi', `/advisor/admin/bookings/${bookingId}`, {
		headers: {},
		response: true,
		queryStringParameters: {}
	});

	return response.data;
});

export const adminMarkNoShow = createAsyncThunk<
	AdvisorBooking,
	{ bookingId: string },
	{ dispatch: AppDispatch; state: RootState }
>('advisorSupport/adminMarkNoShow', async ({ bookingId }) => {
	const response = await API.post('inspirusapi', `/advisor/admin/bookings/${bookingId}/no-show`, {
		headers: {},
		response: true,
		body: {}
	});

	return response.data;
});

export const adminProcessRefund = createAsyncThunk<
	AdvisorBooking,
	{ bookingId: string },
	{ dispatch: AppDispatch; state: RootState }
>('advisorSupport/adminProcessRefund', async ({ bookingId }) => {
	const response = await API.post('inspirusapi', `/advisor/admin/bookings/${bookingId}/refund`, {
		headers: {},
		response: true,
		body: {}
	});

	return response.data;
});

const advisorSupportSlice = createSlice({
	name: 'advisorSupport',
	initialState,
	reducers: {
		resetAdvisorSupport: () => initialState,
		clearSelectedBooking: (state) => {
			state.selectedBooking = null;
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, fetchMeetingTypes, 'meetingTypesRequestStatus', {
			onFulfilled: (state, payload) => {
				state.meetingTypes = payload;
			}
		});

		buildReducers(builder, fetchUserBookings, 'bookingsRequestStatus', {
			onFulfilled: (state, payload) => {
				if (payload.nextToken && state.bookings.length > 0) {
					state.bookings = [...state.bookings, ...payload.items];
				} else {
					state.bookings = payload.items;
				}
				state.bookingsNextToken = payload.nextToken;
			}
		});

		buildReducers(builder, fetchBookingDetail, 'bookingDetailRequestStatus', {
			onFulfilled: (state, payload) => {
				state.selectedBooking = payload;
			}
		});

		buildReducers(builder, fetchGrantStatus, 'grantStatusRequestStatus', {
			onFulfilled: (state, payload) => {
				state.grantStatus = payload;
			}
		});

		buildReducers(builder, cancelBooking, 'cancelBookingRequestStatus', {
			onFulfilled: (state, payload) => {
				const index = state.bookings.findIndex((b: AdvisorBooking) => b.bookingId === payload.bookingId);
				if (index !== -1) {
					state.bookings[index] = payload;
				}
				if (state.selectedBooking?.bookingId === payload.bookingId) {
					state.selectedBooking = payload;
				}
				enqueueSnackbar(i18n.t('pages.advisorSupport.bookingCancelled'), { variant: 'success' });
			}
		});

		// Admin reducers
		buildReducers(builder, fetchAdminBookings, 'adminBookingsRequestStatus', {
			onFulfilled: (state, payload) => {
				if (payload.isNextPage && state.adminBookings.length > 0) {
					state.adminBookings = [...state.adminBookings, ...payload.items];
				} else {
					state.adminBookings = payload.items;
				}
				state.adminBookingsNextToken = payload.nextToken;
			}
		});

		buildReducers(builder, fetchAdminBookingDetail, 'adminBookingDetailRequestStatus', {
			onFulfilled: (state, payload) => {
				state.adminSelectedBooking = payload;
			}
		});

		buildReducers(builder, adminMarkNoShow, 'adminActionRequestStatus', {
			onFulfilled: (state, payload) => {
				state.adminSelectedBooking = payload;
				const index = state.adminBookings.findIndex((b: AdvisorBooking) => b.bookingId === payload.bookingId);
				if (index !== -1) {
					state.adminBookings[index] = payload;
				}
				enqueueSnackbar(i18n.t('pages.advisorSupport.admin.markedNoShow'), { variant: 'success' });
			}
		});

		buildReducers(builder, adminProcessRefund, 'adminActionRequestStatus', {
			onFulfilled: (state, payload) => {
				state.adminSelectedBooking = payload;
				const index = state.adminBookings.findIndex((b: AdvisorBooking) => b.bookingId === payload.bookingId);
				if (index !== -1) {
					state.adminBookings[index] = payload;
				}
				enqueueSnackbar(i18n.t('pages.advisorSupport.admin.refundProcessed'), { variant: 'success' });
			}
		});
	}
});

export const { resetAdvisorSupport, clearSelectedBooking } = advisorSupportSlice.actions;
export default advisorSupportSlice.reducer;
